import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useOutlet } from '../contexts/OutletContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus,
  Edit,
  Search,
  Loader2,
  Users,
  Shield,
  Mail,
  RefreshCw,
} from 'lucide-react';

const getRoles = (t) => [
  { value: 'OWNER', label: t('users.roles.owner'), description: t('users.roles.owner_desc') },
  { value: 'ADMIN', label: t('users.roles.admin'), description: t('users.roles.admin_desc') },
  { value: 'MANAGER', label: t('users.roles.manager'), description: t('users.roles.manager_desc') },
  { value: 'CASHIER', label: t('users.roles.cashier'), description: t('users.roles.cashier_desc') },
  { value: 'KITCHEN', label: t('users.roles.kitchen'), description: t('users.roles.kitchen_desc') },
];

const ROLE_COLORS = {
  OWNER: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/30',
  ADMIN: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30',
  MANAGER: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30',
  CASHIER: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/30',
  KITCHEN: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30',
};

export default function UsersPage() {
  const { t } = useTranslation();
  const { api, user: currentUser } = useAuth();
  const { currentOutlet, outlets } = useOutlet();
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'CASHIER',
    outlet_id: '',
    is_active: true,
  });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get('/users');
      setUsers(resp.data);
    } catch (error) {
      toast.error(t('messages.error.failed_fetch_users'));
    } finally {
      setLoading(false);
    }
  }, [api, t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const openDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        role: user.role,
        outlet_id: user.outlet_id,
        is_active: user.is_active,
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'CASHIER',
        outlet_id: currentOutlet?.id || '',
        is_active: true,
      });
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      if (editingUser) {
        const updates = { ...formData };
        if (!updates.password) delete updates.password;
        await api.put(`/users/${editingUser.id}`, updates);
        toast.success(t('messages.success.user_updated'));
      } else {
        await api.post('/users', formData);
        toast.success(t('messages.success.user_created'));
      }
      setShowDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.error.failed_save_user'));
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await api.put(`/users/${user.id}`, { is_active: !user.is_active });
      toast.success(t(!user.is_active ? 'messages.success.user_activated' : 'messages.success.user_deactivated'));
      fetchUsers();
    } catch (error) {
      toast.error(t('messages.error.failed_update_user'));
    }
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const canManage = ['OWNER', 'ADMIN', 'MANAGER'].includes(currentUser?.role);
  const canCreateAdmin = ['OWNER', 'ADMIN'].includes(currentUser?.role);

  const getOutletName = (outletId) => {
    return outlets.find(o => o.id === outletId)?.name || 'Unknown';
  };

  return (
    <div className="p-6 space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('users.title')}</h1>
          <p className="text-muted-foreground">{t('users.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchUsers} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t('common.refresh')}
          </Button>
          {canManage && (
            <Button onClick={() => openDialog()} data-testid="add-user-btn">
              <Plus className="h-4 w-4 mr-2" />
              {t('users.add_user')}
            </Button>
          )}
        </div>
      </div>

      {/* Role Legend */}
      <div className="flex flex-wrap gap-3">
        {getRoles(t).map((role) => (
          <Badge key={role.value} variant="outline" className={ROLE_COLORS[role.value]}>
            <Shield className="h-3 w-3 mr-1" />
            {role.label}
          </Badge>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={t('users.search_placeholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="user-search"
        />
      </div>

      {/* Users Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : filteredUsers.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64">
            <Users className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">{t('empty_states.no_users')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredUsers.map((user) => (
            <Card key={user.id} className={!user.is_active ? 'opacity-60' : ''} data-testid={`user-${user.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground text-lg font-bold">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-semibold">{user.name}</h3>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {user.email}
                      </p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        <Badge variant="outline" className={ROLE_COLORS[user.role]}>
                          {user.role}
                        </Badge>
                        {!user.is_active && (
                          <Badge variant="destructive">{t('users.inactive')}</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('users.outlet')}: {getOutletName(user.outlet_id)}
                      </p>
                    </div>
                  </div>
                  {canManage && user.id !== currentUser?.id && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openDialog(user)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingUser ? t('users.edit_user') : t('users.add_user')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('users.full_name')} *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('users.full_name')}
                data-testid="user-name-input"
              />
            </div>
            <div>
              <Label>{t('users.email')} *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="user@example.com"
                disabled={!!editingUser}
                data-testid="user-email-input"
              />
            </div>
            <div>
              <Label>{editingUser ? t('users.new_password_hint') : t('users.password') + ' *'}</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                data-testid="user-password-input"
              />
            </div>
            <div>
              <Label>{t('users.role')}</Label>
              <Select
                value={formData.role}
                onValueChange={(val) => setFormData({ ...formData, role: val })}
              >
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getRoles(t).map((role) => (
                    <SelectItem
                      key={role.value}
                      value={role.value}
                      disabled={['OWNER', 'ADMIN'].includes(role.value) && !canCreateAdmin}
                    >
                      <div>
                        <span className="font-medium">{role.label}</span>
                        <span className="text-xs text-muted-foreground ml-2">- {role.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('users.outlet')}</Label>
              <Select
                value={formData.outlet_id}
                onValueChange={(val) => setFormData({ ...formData, outlet_id: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('form.select_outlet')} />
                </SelectTrigger>
                <SelectContent>
                  {outlets.map((outlet) => (
                    <SelectItem key={outlet.id} value={outlet.id}>
                      {outlet.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {editingUser && editingUser.id !== currentUser?.id && (
              <div className="flex items-center justify-between p-3 bg-muted rounded-md">
                <span className="text-sm">{t('users.account_status')}</span>
                <Button
                  variant={formData.is_active ? 'destructive' : 'default'}
                  size="sm"
                  onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                >
                  {formData.is_active ? t('users.deactivate') : t('users.activate')}
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSave} data-testid="save-user-btn">
              {editingUser ? t('users.save_changes') : t('users.create_user')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
